import { useState } from "react";
import emailjs from "@emailjs/browser";
import { PROFILE } from "../data/constants";
import { IconMail, IconBriefcase, IconBranch, IconWarn, IconArrow } from "./icons";

// ─────────────────────────────────────────────────────────────────────────────
// EMAILJS CONFIG — see EMAILJS_SETUP.txt
// ponytail: public key is client-side by design; EmailJS rate-limits per key.
// Move to env vars (REACT_APP_*) if you ever need to rotate without a rebuild.
// ─────────────────────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = "service_zxc1der";
const EMAILJS_TEMPLATE_ID = "template_vvqhg0r";
const EMAILJS_PUBLIC_KEY  = "LX4SklOVC9CtG3YI-";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

export const validate = ({ name, email, message }) => ({
  name:    name.trim().length    < 2  ? "Enter your name."                        : "",
  email:   !EMAIL_RE.test(email.trim()) ? "Enter a valid email address."           : "",
  message: message.trim().length < 10 ? "Tell me a little more — 10 characters minimum." : "",
});

const CHANNELS = [
  { Icon: IconMail,      label: "email",    value: PROFILE.email,               href: `mailto:${PROFILE.email}` },
  { Icon: IconBriefcase, label: "linkedin", value: "linkedin.com/in/nisarg0330", href: PROFILE.linkedin },
  { Icon: IconBranch,    label: "github",   value: "github.com/nisarg0330",      href: PROFILE.github },
];

function ContactForm() {
  const [form, setForm]       = useState({ name: "", email: "", message: "" });
  const [touched, setTouched] = useState({});
  const [status, setStatus]   = useState("idle");   // idle | sending | sent | error

  const errors  = validate(form);
  const hasErrors = Object.values(errors).some(Boolean);
  const show = (k) => (touched[k] && errors[k]) || "";

  const set  = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (hasErrors || status === "sending") return;

    setStatus("sending");
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_name:   form.name,
          to_email:  form.email,
          from_name: PROFILE.name,
          message:   form.message,
        },
        EMAILJS_PUBLIC_KEY
      );
      setStatus("sent");
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
    }
  };

  /* ── Sent ─────────────────────────────────────────────────────────────── */
  if (status === "sent") {
    return (
      <div className="panel" style={{ padding: 30 }}>
        <p className="sec-marker" style={{ marginBottom: 14 }}>{"// 200 OK"}</p>
        <h3 className="h3" style={{ fontSize: 18 }}>Message sent.</h3>
        <p className="prose" style={{ marginTop: 12 }}>
          Thanks, <span className="t-hi">{form.name}</span>. A confirmation is on its way to{" "}
          <span className="t-str">{form.email}</span>. I reply within a day, usually sooner.
        </p>
        <button
          className="btn"
          style={{ marginTop: 24 }}
          onClick={() => { setForm({ name: "", email: "", message: "" }); setTouched({}); setStatus("idle"); }}
        >
          Write another
        </button>
      </div>
    );
  }

  /* ── Sending — skeleton matching the form's own shape ─────────────────── */
  if (status === "sending") {
    return (
      <div className="panel" style={{ padding: 30 }}>
        <p className="sec-marker" style={{ marginBottom: 20 }}>{"// sending…"}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="skeleton" style={{ width: "34%" }} />
          <div className="skeleton" style={{ width: "78%" }} />
          <div className="skeleton" style={{ width: "56%" }} />
          <div className="skeleton" style={{ width: "88%" }} />
          <div className="skeleton" style={{ width: "42%" }} />
        </div>
      </div>
    );
  }

  /* ── Form ─────────────────────────────────────────────────────────────── */
  return (
    <form className="panel" style={{ padding: 30 }} onSubmit={handleSubmit} noValidate>
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>

        <div className="field">
          <label className="field-label" htmlFor="cf-name">Name</label>
          <input
            id="cf-name" className="input" type="text" autoComplete="name"
            placeholder="Priya Raghunathan"
            value={form.name} onChange={set("name")} onBlur={blur("name")}
            aria-invalid={!!show("name")}
            aria-describedby={show("name") ? "cf-name-err" : undefined}
          />
          {show("name") && <span className="field-error" id="cf-name-err">{errors.name}</span>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="cf-email">Email</label>
          <input
            id="cf-email" className="input" type="email" autoComplete="email"
            placeholder="priya@fieldnote.dev"
            value={form.email} onChange={set("email")} onBlur={blur("email")}
            aria-invalid={!!show("email")}
            aria-describedby={show("email") ? "cf-email-err" : undefined}
          />
          {show("email") && <span className="field-error" id="cf-email-err">{errors.email}</span>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="cf-message">Message</label>
          <textarea
            id="cf-message" className="input" rows={4}
            placeholder="We're hiring a junior backend developer and your Blackjack repo caught my eye…"
            value={form.message} onChange={set("message")} onBlur={blur("message")}
            aria-invalid={!!show("message")}
            aria-describedby={show("message") ? "cf-message-err" : undefined}
          />
          {show("message") && <span className="field-error" id="cf-message-err">{errors.message}</span>}
        </div>

        {status === "error" && (
          <p className="alert" role="alert">
            <IconWarn width={14} height={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              The message could not be sent. Email me directly at{" "}
              <a href={`mailto:${PROFILE.email}`} style={{ color: "var(--accent)" }}>{PROFILE.email}</a>.
            </span>
          </p>
        )}

        <button type="submit" className="btn btn-accent" style={{ width: "100%" }}>
          Send message <IconArrow width={13} height={13} />
        </button>
      </div>
    </form>
  );
}

export default function Contact() {
  return (
    <section id="contact" className="sec" style={{ paddingBottom: 96 }}>
      <p className="sec-marker reveal">{"// get in touch"}</p>

      <div className="grid-contact">
        <div>
          <h2 className="h2 reveal d1">
            Let's build<br /><span className="ghost">something.</span>
          </h2>

          <p className="prose reveal d2" style={{ marginTop: 20 }}>
            Looking for internship and entry-level engineering roles in the Toronto area or remote.
            Open to contract work and to talking through a problem with no strings attached.
          </p>

          <div className="side-meta reveal d3" style={{ marginTop: 32 }}>
            {CHANNELS.map(({ Icon, label, value, href }) => (
              <a key={label} className="side-meta-row" href={href} target="_blank" rel="noreferrer">
                <span className="ico"><Icon /></span>
                <span><span className="t-com">{label}:</span> {value}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="reveal d2">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}
